/* prettier-ignore-file */
/**
 * Shared rental pricing engine (ALL MONEY IN CENTS).
 *
 * Single source of truth for BookingWizard.js and RentalCalculatorModal.js so a
 * quote and an estimate can never drift apart.
 *
 * Mirrors computeBookingCosts() in EditBooking.js, the canonical formulation.
 * NewBooking.js's inline math was reconciled to it 2026-10-05 ("user-selection"
 * add-ons × days, delivery-discount tiers matched numerically in cents, miles
 * rounded to 1 decimal, midnight-normalized day counts). If a surface disagrees
 * with this engine again, that surface is the bug.
 */

import {
  calculateTransportationDiscountUtil,
  calculateDurationDiscountUtil,
} from "./discountCalculations";

/** Parse Date | Firestore Timestamp | serialized Timestamp | ISO string -> Date|null */
export const parseRentalDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return isNaN(date.getTime()) ? null : date;
  if (typeof date.toDate === "function") return date.toDate();
  if (date?.type === "firestore/timestamp/1.0" && date?.seconds !== undefined) {
    return new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
  }
  if (date?.seconds !== undefined) return new Date(date.seconds * 1000);
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Rental length in days ("nights"): the midnight-to-midnight difference, rounded.
 * Round, not ceil: both ends are local midnight, so the only non-whole-day case
 * is the DST hour — ceil turned November's 25-hour fall-back day into an extra
 * billed day. Matches NewBooking.js and EditBooking.js, including the `|| 1`
 * floor so a same-day range still prices as one day rather than zero.
 */
export const getRentalDays = (startDate, endDate) => {
  const start = parseRentalDate(startDate);
  const end = parseRentalDate(endDate);
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  return Math.round((e - s) / (1000 * 60 * 60 * 24)) || 1;
};

/**
 * Every pricing knob, read from the Firestore settings collection.
 * Mirrors the canonical read block at NewBooking.js:150-173.
 */
export const readPricingSettings = (settings) => ({
  salesTaxPercent: Number(settings?.salesTax?.percent) || 0,
  bookingPlatformFeeCents: Number(settings?.bookingPlatform?.perBookingFee) || 0,
  cardProcessingFeePercent: Number(settings?.cardProcessingFee?.percent ?? 3) || 0,
  cardProcessingFeeForPurchases: settings?.cardProcessingFee?.forPurchases ?? false,
  // Both already stored in CENTS -- do not convert. See NewBooking.js:1424.
  mileageRateCents: Number(settings?.mileageRate?.rate) || 0,
  loadingFeeCents: Number(settings?.loadingFee?.fee) || 0,
  startingAddress: settings?.startingAddress?.address ?? "",
  useDeliveryDiscounts: settings?.useDeliveryDiscounts?.enabled ?? true,
  deliveryDiscounts: settings?.deliveryDiscounts?.discounts ?? [],
  useDurationDiscounts: settings?.useDurationDiscounts?.enabled ?? false,
  durationDiscounts: settings?.durationDiscounts?.discounts ?? [],
  product1Title: settings?.product1Title?.title ?? "Bicycles",
  product2Title: settings?.product2Title?.title ?? "Add-Ons",
});

/**
 * Equipment subtotal in cents.
 *
 * product1 line items: price x quantity x days
 * product2 line items, by catalog `option`:
 *   per-booking    -> price x quantity
 *   per-bike       -> price x quantity x totalBikes
 *   user-selection -> price x quantity x days
 *
 * @param {Array} product1 [{ id, quantity }]
 * @param {Array} product2 [{ id, quantity }]
 */
export const computeEquipmentCostCents = ({
  product1 = [],
  product2 = [],
  products1Catalog = [],
  products2Catalog = [],
  days = 1,
  // Admin-negotiated per-unit daily rate. When set, every Product1 item bills at this
  // instead of its catalog price; quantity and days still multiply as normal.
  // Product2 add-ons are unaffected — this overrides the DAILY RATE, not the order.
  dailyRateOverrideCents = null,
}) => {
  let product1Cents = 0;
  let product2Cents = 0;

  const hasOverride = Number.isFinite(dailyRateOverrideCents) && dailyRateOverrideCents >= 0;
  const totalBikes = product1.reduce((sum, item) => sum + (item.quantity || 0), 0);

  product1.forEach((item) => {
    if (!(item.quantity > 0)) return;
    const catalogProduct = products1Catalog.find((p) => p.id === item.id);
    if (!catalogProduct) return;
    const unitCents = hasOverride ? dailyRateOverrideCents : catalogProduct.price || 0;
    product1Cents += unitCents * item.quantity * days;
  });

  product2.forEach((item) => {
    if (!(item.quantity > 0)) return;
    const catalogProduct = products2Catalog.find((p) => p.id === item.id);
    if (!catalogProduct) return;
    const price = catalogProduct.price || 0;
    const option = catalogProduct.option || "per-booking";
    if (option === "per-booking") {
      product2Cents += price * item.quantity;
    } else if (option === "per-bike") {
      product2Cents += price * item.quantity * totalBikes;
    } else {
      // "user-selection" -- billed per day. See header note #1.
      product2Cents += price * item.quantity * days;
    }
  });

  return { product1Cents, product2Cents, totalCents: product1Cents + product2Cents, totalBikes };
};

/**
 * One-way transportation cost in cents: (miles x rate) + loading fee.
 * When distance is unknown but a loading fee exists, the fee alone is charged.
 * Matches NewBooking.js:1451-1455 and EditBooking.js:1824-1830.
 */
export const computeOneWayTransportCents = ({ miles, mileageRateCents, loadingFeeCents }) => {
  const distance = Number(miles) || 0;
  const rate = Number(mileageRateCents) || 0;
  const fee = Number(loadingFeeCents) || 0;
  if (distance > 0 && rate > 0) return Math.round(distance * rate) + fee;
  return fee;
};

/**
 * Full rental quote. Every returned *Cents value is an integer number of cents.
 *
 * Delivery and home pickup are independent legs; each costs a full one-way
 * amount, so selecting both doubles the transportation charge.
 */
export const computeRentalQuote = ({
  startDate,
  endDate,
  product1 = [],
  product2 = [],
  products1Catalog = [],
  products2Catalog = [],
  settings = {},
  delivery = false,
  homePickup = false,
  miles = null,
  dailyRateOverrideCents = null,
}) => {
  const cfg = readPricingSettings(settings);
  const days = getRentalDays(startDate, endDate);
  const hasRateOverride =
    Number.isFinite(dailyRateOverrideCents) && dailyRateOverrideCents >= 0;

  const equipment = computeEquipmentCostCents({
    product1,
    product2,
    products1Catalog,
    products2Catalog,
    days,
    dailyRateOverrideCents,
  });
  const productCostCents = equipment.totalCents;

  // --- Transportation -------------------------------------------------------
  const oneWayCents = computeOneWayTransportCents({
    miles,
    mileageRateCents: cfg.mileageRateCents,
    loadingFeeCents: cfg.loadingFeeCents,
  });
  const deliveryCostCents = delivery ? oneWayCents : 0;
  const pickupCostCents = homePickup ? oneWayCents : 0;
  const transportTotalCents = deliveryCostCents + pickupCostCents;

  // --- Discounts ------------------------------------------------------------
  // Both utils take a booking-shaped object, so build one from the draft.
  const draftBooking = {
    product1,
    product2,
    startDate,
    endDate,
    delivery,
    homePickup,
  };

  // The duration discount applies to every booking, negotiated rate or not.
  //
  // Only the PERCENT is taken from the util. Its own discountAmountCents is
  // derived from catalog prices (discountCalculations.js:219-228), so on an
  // overridden booking it would discount against a base the customer is not
  // being charged -- $50 catalog vs a $30 override turns a 10% tier into an
  // effective 16.7%. Tier selection is purely day-based, so the percent itself
  // is unaffected by the override and is the part worth reusing.
  //
  // Applied to product1 only, matching the util: duration discounts are for
  // bicycles, not add-ons. With no override this yields exactly what the util
  // would have returned, because product1Cents is then the same catalog sum.
  const durationDiscount = calculateDurationDiscountUtil({
    booking: draftBooking,
    durationDiscounts: cfg.durationDiscounts,
    products1Catalog,
    products2Catalog,
    useDurationDiscounts: cfg.useDurationDiscounts,
  });
  const durationDiscountCents = Math.round(
    equipment.product1Cents * ((durationDiscount.discountPercent || 0) / 100)
  );

  const transportationDiscount = calculateTransportationDiscountUtil({
    settings,
    newBooking: draftBooking,
    products1Catalog,
    products2Catalog,
  });
  const transportationDiscountPercent = transportationDiscount.discountPercent || 0;
  const transportationDiscountCents = Math.round(
    transportTotalCents * (transportationDiscountPercent / 100)
  );

  // Split the transportation discount proportionally back onto each leg so the
  // booking document can store per-leg discounted values (NewBooking.js:1594-1609).
  let deliveryWithDiscountCents = deliveryCostCents;
  let pickupWithDiscountCents = pickupCostCents;
  if (transportationDiscountCents > 0 && transportTotalCents > 0) {
    const deliveryShare = Math.round(
      transportationDiscountCents * (deliveryCostCents / transportTotalCents)
    );
    deliveryWithDiscountCents = deliveryCostCents - deliveryShare;
    pickupWithDiscountCents = pickupCostCents - (transportationDiscountCents - deliveryShare);
  }

  // --- Subtotals, fees, tax -------------------------------------------------
  const subtotalWithoutDiscountCents = productCostCents + transportTotalCents;
  const subtotalWithDiscountCents =
    subtotalWithoutDiscountCents - transportationDiscountCents - durationDiscountCents;

  // An empty basket is not a booking, so it carries no fees. Without this the
  // per-booking platform fee lands the moment dates are picked and — once tax and card
  // processing compound on top of it — a total appears for an order with nothing in it.
  // Gating the platform fee is enough: with it at zero the subtotal is zero, so tax and
  // the card fee both compute to zero and the whole quote collapses to $0.00.
  const hasOrder = productCostCents > 0 || transportTotalCents > 0;
  const platformFeeCents = hasOrder ? cfg.bookingPlatformFeeCents : 0;

  // Tax applies to the discounted subtotal PLUS the platform fee, but not the card fee.
  const salesTaxCents = Math.round(
    (subtotalWithDiscountCents + platformFeeCents) * (cfg.salesTaxPercent / 100)
  );

  const cardProcessingFeeCents =
    cfg.cardProcessingFeeForPurchases && cfg.cardProcessingFeePercent > 0
      ? Math.round(
          (subtotalWithDiscountCents + platformFeeCents + salesTaxCents) *
            (cfg.cardProcessingFeePercent / 100)
        )
      : 0;

  const finalTotalCents =
    subtotalWithDiscountCents + platformFeeCents + salesTaxCents + cardProcessingFeeCents;

  return {
    days,
    totalBikes: equipment.totalBikes,
    dailyRateOverrideCents: hasRateOverride ? dailyRateOverrideCents : null,

    product1Cents: equipment.product1Cents,
    product2Cents: equipment.product2Cents,
    productCostCents,

    miles: Number(miles) || 0,
    oneWayCents,
    deliveryCostCents,
    pickupCostCents,
    transportTotalCents,
    deliveryWithDiscountCents,
    pickupWithDiscountCents,

    durationDiscountCents,
    durationDiscountPercent: durationDiscount.discountPercent || 0,
    durationDiscountName: durationDiscount.discountName || "",
    transportationDiscountCents,
    transportationDiscountPercent,
    transportationDiscountName: transportationDiscount.discountName || "",

    subtotalWithoutDiscountCents,
    subtotalWithDiscountCents,
    platformFeeCents,
    salesTaxCents,
    salesTaxPercent: cfg.salesTaxPercent,
    cardProcessingFeeCents,
    cardProcessingFeePercent: cfg.cardProcessingFeePercent,

    finalTotalCents,
  };
};

/** Minimum order total enforced before checkout, in cents (NewBooking.js:2235). */
export const MINIMUM_ORDER_CENTS = 200;
